import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronUp, ChevronDown, ChevronRight, ClipboardList } from 'lucide-react';
import { api, parseApiError, type SeedingTableRow } from '../lib/api';
import { displayClanName } from '../lib/displayName';
import { HelpText } from './HelpText';

// ─────────────────────────────────────────────────────────────────────────────
// SeedingTablePanel — the SEEDING stage's runtime (src/shared/seedingTable.ts):
// an ordered list of enrolled teams, rendered INSIDE StagePipelinePanel's
// SEEDING stage card (below its config) whenever that stage is PENDING or
// LIVE — the parent decides when to mount this; it never checks stage state
// itself. Teams auto-append here the instant they enroll (any of the comp's
// three enrollment doors); an admin/org-or-comp-manager may then move
// any team to any position at any time — no lock window beyond the stage
// itself reaching ENDED (Lisa's anti-sandbagging requirement — see the
// backend module's header for the full product model).
//
// Every change writes an append-only audit-log row server-side — the
// differentiator. The "Change log" section below is collapsed by default and
// only fetches (?log=1) once opened, same lazy-query idiom as
// StagePipelineCard's DIVISIONS enrolled-clan-count query.
// ─────────────────────────────────────────────────────────────────────────────

// Never show a raw discordId — same local shortId() idiom OrgMembersPanel/
// OrgProposalsPanel/OrgOwnerBoardPanel already use when a name can't be
// resolved (the backend already resolves what it can; this is the last-resort
// fallback for whatever's left).
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function formatLogTime(at: number): string {
  return new Date(at).toLocaleString('en-ZA', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function SeedingTablePanel({ compId }: { compId: string }) {
  const qc = useQueryClient();
  const queryKey = ['seedingTable', compId];
  const logQueryKey = ['seedingTable', compId, 'log'];

  const { data } = useQuery({
    queryKey,
    queryFn: () => api.getSeedingTable(compId),
    enabled: !!compId,
  });
  const rows = (data?.rows ?? []).slice().sort((a, b) => a.seed - b.seed);

  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteUnmatched, setPasteUnmatched] = useState<string[]>([]);
  const [logOpen, setLogOpen] = useState(false);

  const logQuery = useQuery({
    queryKey: logQueryKey,
    queryFn: () => api.getSeedingTable(compId, { log: true }),
    enabled: logOpen && !!compId,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey });
    if (logOpen) qc.invalidateQueries({ queryKey: logQueryKey });
  };

  const orderMut = useMutation({
    mutationFn: (entries: Array<{ clanId: string; teamNo?: number }>) => api.orderSeedingTable(compId, entries, reason.trim() || undefined),
    onSuccess: () => {
      setError('');
      setPasteOpen(false);
      setPasteText('');
      setPasteUnmatched([]);
      invalidateAll();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // "Paste order" — one clan name per line, resolved against the CURRENT
  // table's rows by name (case-insensitive, matching either the raw stored
  // name or its cleaned-up display form — see displayClanName). Unmatched
  // lines are reported and simply excluded from the order call (the partial-
  // list rule on the server keeps every excluded/unmatched clan's relative
  // order, placed after every successfully-matched one).
  const applyPasteOrder = () => {
    const lines = pasteText.split('\n').map((l) => l.trim()).filter(Boolean);
    // M2: match against the row's qualified clanName (a team row's is
    // already "TeamName (ClanName)") and carry its teamNo along so a
    // multi-team clan's rows resolve unambiguously server-side.
    const matchedEntries: Array<{ clanId: string; teamNo?: number }> = [];
    const unmatched: string[] = [];
    for (const line of lines) {
      const lower = line.toLowerCase();
      const row = rows.find((r) => r.clanName.toLowerCase() === lower || displayClanName(r.clanName).toLowerCase() === lower);
      if (row) matchedEntries.push({ clanId: row.clanId, ...(row.teamNo != null ? { teamNo: row.teamNo } : {}) });
      else unmatched.push(line);
    }
    setPasteUnmatched(unmatched);
    if (matchedEntries.length > 0) orderMut.mutate(matchedEntries);
  };

  if (!compId) return null;

  return (
    <div className="mt-3 border-t border-white/5 pt-5">
      <p className="mb-2 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">Seeding Table</p>
      <HelpText>
        <p className="mb-2 text-[11px] text-ink-meta">Teams append here as they enter the competition. Type a new seed number or nudge with the arrows to reorder — every move writes to the change log below.</p>
      </HelpText>

      <div className="mb-2 flex items-center gap-2">
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Optional reason — noted in the change log"
          maxLength={300}
          className="input flex-1 text-xs"
        />
        <button
          type="button"
          onClick={() => setPasteOpen((o) => !o)}
          className="btn-ghost inline-flex shrink-0 items-center gap-1 px-2 py-1 text-xs"
        >
          <ClipboardList className="h-3.5 w-3.5" /> Paste order
        </button>
      </div>

      {pasteOpen && (
        <div className="mb-2 rounded-lg bg-white/[0.04] p-2.5">
          <p className="mb-1.5 text-[11px] text-ink-meta">One team name per line, in the order you want them seeded.</p>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={5}
            className="input w-full resize-none text-xs"
            placeholder={rows.slice(0, 3).map((r) => displayClanName(r.clanName)).join('\n')}
          />
          {pasteUnmatched.length > 0 && (
            <p className="mt-1.5 text-[11px] text-red-400">Not matched to a team: {pasteUnmatched.join(', ')}</p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <button
              type="button"
              disabled={orderMut.isPending || !pasteText.trim()}
              onClick={applyPasteOrder}
              className="btn-primary px-2 py-0.5 text-[11px] disabled:opacity-50"
            >
              {orderMut.isPending ? 'Applying…' : 'Apply order'}
            </button>
            <button
              type="button"
              onClick={() => { setPasteOpen(false); setPasteText(''); setPasteUnmatched([]); }}
              className="text-[11px] text-ink-meta hover:text-ink-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-xs text-ink-meta">Teams appear here as they enter the competition.</p>
      ) : (
        <div className="space-y-1">
          {rows.map((row, i) => (
            <SeedingTableRowItem
              // M2: a multi-team clan holds several rows sharing the SAME
              // clanId — key off the row's full identity (clanId + teamNo)
              // so React never conflates Beta's row with Charlie's.
              key={`${row.clanId}-${row.teamNo ?? 'legacy'}`}
              compId={compId}
              row={row}
              isFirst={i === 0}
              isLast={i === rows.length - 1}
              reason={reason}
              onChanged={invalidateAll}
            />
          ))}
        </div>
      )}

      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}

      <div className="mt-2">
        <button
          type="button"
          onClick={() => setLogOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-[11px] text-ink-meta hover:text-ink-primary"
        >
          {logOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Change log
        </button>
        {logOpen && (
          <ul className="mt-1.5 space-y-1">
            {(logQuery.data?.log ?? []).length === 0 && (
              <li className="text-[11px] text-ink-meta">No changes yet.</li>
            )}
            {(logQuery.data?.log ?? []).map((entry, i) => (
              <li key={`${entry.at}-${entry.clanId}-${i}`} className="text-[11px] text-ink-meta">
                <span className="text-ink-primary">{entry.actorName ?? shortId(entry.actorDiscordId)}</span>
                {' moved '}
                <span className="text-ink-primary">{displayClanName(entry.clanName)}</span>
                {' '}
                {entry.fromSeed ?? '—'}→{entry.toSeed} · {formatLogTime(entry.at)}
                {entry.reason ? ` · ${entry.reason}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── SeedingTableRowItem — one row, its own hooks ────────────────────────────
// Extracted so the list `.map()` above never puts hooks in a loop — each row
// owns its own move mutation + its own "type a number" draft state
// independently (same idiom StagePipelineCard uses for the stage list above).
function SeedingTableRowItem({
  compId,
  row,
  isFirst,
  isLast,
  reason,
  onChanged,
}: {
  compId: string;
  row: SeedingTableRow;
  isFirst: boolean;
  isLast: boolean;
  reason: string;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState(String(row.seed));
  const [error, setError] = useState('');

  // Keep the draft box in sync whenever the server's seed for this row
  // changes (this row's own successful move, another admin's move shifting
  // it collaterally, or a bulk reorder).
  useEffect(() => {
    setDraft(String(row.seed));
  }, [row.seed]);

  const moveMut = useMutation({
    // M2: pass the row's OWN teamNo so a multi-team clan's move disambiguates
    // server-side instead of 400ing ambiguous_entry.
    mutationFn: (toSeed: number) => api.moveSeedingClan(compId, row.clanId, toSeed, reason.trim() || undefined, row.teamNo),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => { setError(parseApiError(e).message); setDraft(String(row.seed)); },
  });

  const commitDraft = () => {
    const parsed = Number(draft);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed === row.seed) {
      setDraft(String(row.seed));
      return;
    }
    moveMut.mutate(parsed);
  };

  return (
    <div className="flex items-center gap-2 rounded-lg bg-white/[0.04] px-2.5 py-1.5">
      <span className="w-6 shrink-0 text-right text-xs font-medium text-ink-meta">{row.seed}.</span>
      <span className="min-w-0 flex-1 truncate text-sm text-ink-primary">{displayClanName(row.clanName)}</span>
      <input
        type="number"
        min={1}
        value={draft}
        disabled={moveMut.isPending}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="input w-12 shrink-0 text-center text-xs disabled:cursor-not-allowed disabled:opacity-50"
      />
      <button
        type="button"
        title="Move up"
        disabled={isFirst || moveMut.isPending}
        onClick={() => moveMut.mutate(row.seed - 1)}
        className="rounded p-1 text-ink-meta transition hover:bg-white/[0.06] hover:text-ink-primary disabled:opacity-30"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        title="Move down"
        disabled={isLast || moveMut.isPending}
        onClick={() => moveMut.mutate(row.seed + 1)}
        className="rounded p-1 text-ink-meta transition hover:bg-white/[0.06] hover:text-ink-primary disabled:opacity-30"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {error && <span className="shrink-0 text-[11px] text-red-400">{error}</span>}
    </div>
  );
}
